import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '@/api/client'
import { BriefView } from '@/components/BriefForm'
import { BriefDownload } from '@/components/BriefDownload'
import { CoachPanel } from '@/components/CoachPanel'
import { NextStep } from '@/components/NextStep'
import { Picker } from '@/components/Picker'
import { RubricView } from '@/components/RubricView'
import { SubmissionPanel } from '@/components/SubmissionPanel'
import { TaskHeading, TaskTabs } from '@/components/TaskHeading'
import { DraftReview } from '@/components/reviews/DraftReview'
import { useAsync } from '@/components/useAsync'
import type { FileItem, Role, Task, TaskDetail } from '@/lib/types'
import { PipelineException, WorkflowBuilder } from './WorkflowBuilder'
import { WorkflowProgress } from './WorkflowProgress'
import { WORKFLOW_STAGES, workflowStage } from '@/lib/stages'
import './scientific.css'

type Submission = { workflow: Task; examples: { task: Task; files: FileItem[] }[] }

/** The worked examples a workflow drafted before the template flow was submitted with. Read-only. */
export function WorkflowExamples({ workflowId }: { workflowId: string }) {
  const source = useAsync(() => api.get<Submission>(`/scientific/workflows/${workflowId}/submission`), [workflowId])
  const [selected, setSelected] = useState('')
  const data = source.data
  const example = data?.examples.find(e => e.task.task_id === selected) ?? data?.examples[0]
  if (source.error) return <p className="note err" role="alert">{source.error}<button onClick={source.reload}>Retry</button></p>
  if (!data) return <p role="status">Loading examples…</p>
  return <section>
    <div className="panel-section-head"><h2>Worked examples</h2></div>
    {!!data.examples.length && <div className="workflow-example-picker"><Picker field label="Worked example" value={example?.task.task_id ?? ''} onChange={setSelected}
      options={data.examples.map(e => ({ value: e.task.task_id, label: e.task.title }))} /></div>}
    {example && <>
      <section className="box">
        <h3>{example.task.title}</h3>
        <h4>Exact prompt</h4><p className="prewrap">{example.task.prompt}</p>
        <h4>Reference result</h4><p className="prewrap">{example.task.submission.result}</p>
        <h3>Example rubric</h3><RubricView rubric={example.task.rubric} layout={example.task.rubric_layout} />
      </section>
      <SubmissionPanel task={example.task} files={example.files} editable={false} onChanged={source.reload} scientific />
    </>}
  </section>
}

export default function WorkflowAuthoring({ data, role, onSaved, onDirty }: {
  data: TaskDetail; role: Role; onSaved: (value: TaskDetail) => void; onDirty: (value: boolean) => void
}) {
  const { task } = data
  const [params] = useSearchParams()
  const section = params.get('section') ?? 'work'
  const [error, setError] = useState('')
  const refresh = () => { void api.get<TaskDetail>(`/tasks/${task.task_id}`).then(onSaved).catch(e => setError(e.message)) }
  const retired = task.lifecycle === 'retired'
  return <div className="task-page">
    <TaskHeading task={task} role={role} />
    <p className="note" role="status">{task.stage_info.label}</p>
    {/* Stages 2 and 3 of the board belong to the template a workflow came from, not to the
        workflow, so this stepper shows the four a workflow itself passes through. */}
    <div className="stepper" aria-label="Scientific workflow stages">{WORKFLOW_STAGES.map(({ key, num, label }) =>
      <div key={key} className={`step ${key === workflowStage(task) ? 'current' : ''}`}><span className="n">{num}</span><span className="lbl">{label}</span></div>)}</div>
    <TaskTabs scientific stage={task.stage} section={section} items={['work', 'brief', 'rubric', 'coach']} />
    {error && <p className="note err" role="alert">{error}</p>}
    {section === 'brief' ? <section className="box"><h2>Workflow brief</h2><BriefView brief={task.brief} /><BriefDownload taskId={task.task_id} /></section>
      : section === 'rubric' ? <section className="box"><h2>Shared workflow rubric</h2><RubricView rubric={task.rubric} layout={task.rubric_layout} /></section>
      : section === 'coach' ? <CoachPanel taskId={task.task_id} role={role} readOnly={retired || task.stage === 3} />
      : <>
        {task.last_feedback && task.last_feedback.to_stage === task.stage && <p className="note">{task.last_feedback.message}</p>}
        {task.stage === 0 && !retired && <DraftReview task={task} role={role} refresh={refresh} onDirty={onDirty} />}
        {!!task.scientific_authoring?.example_ids?.length && <WorkflowExamples workflowId={task.task_id} />}
        {task.stage === 3 && <section className="box"><h2>Build progress</h2>
          <p>An agent builds the tasks and their graders from the approved research and grades them. The quality check opens on its own when that is done.</p>
          <WorkflowProgress workflowId={task.task_id} revision={task.revision ?? 0} onChanged={refresh} />
          {role === 'engineer' && !retired && <>
            <PipelineException exception={task.science_exception} onRetried={refresh} />
            <WorkflowBuilder workflowId={task.task_id} revision={task.revision ?? 0} compiled={false} onChanged={refresh}
              builder={task.scientific_authoring?.builder ? task.authoring : null} />
          </>}
        </section>}
        {!retired && <NextStep task={task} actions={data.actions} role={role} onDone={onSaved} />}
      </>}
  </div>
}
